import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './RegistrationForm.css';
function getOrCreateSessionToken() {
    if (typeof window === 'undefined') return null;
    let token = window.localStorage.getItem('study_session_token');
    if (!token) {
        // crypto.randomUUID is supported in modern browsers; if not, you could fallback
        token = crypto.randomUUID();
        window.localStorage.setItem('study_session_token', token);
    }
    return token;
}

function ScreeningPage() {
    const navigate = useNavigate();
    const {search} = useLocation();
    const params = new URLSearchParams(search || '');
    const interfaceParam = params.get('interface') || params.get('mode') || 'write';

    const [form, setForm] = useState({
        age18: '',            // 'yes' | 'no'
        nativeEnglish: '',    // 'yes' | 'no'
        readingLevel: '',     // '1' - '5'
        writingLevel: '',     // '1' - '5'
    });

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [blocked, setBlocked] = useState(false);

    const updateField = (field, value) =>
        setForm(prev => ({...prev, [field]: value}));

    const handleBackHome = () => navigate('/');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Required: all fields
        if (!form.age18 || !form.nativeEnglish || !form.readingLevel || !form.writingLevel) {
            setError('Please answer all questions before continuing.');
            return;
        }

        // Under 18 → hard stop, no navigation
        if (form.age18 === 'no') {
            setBlocked(true);
            setError('You must be 18 years of age or older to participate in this study.');
            return;
        }

        setSubmitting(true);
        try {
            const sessionToken = getOrCreateSessionToken();
            // Store in DB (implement on backend)
            const res = await fetch('/api/screening', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    interface: interfaceParam,
                    group: interfaceParam === 'annotate' ? 'evaluator' : 'creator',
                    age_18_plus: form.age18 === 'yes',
                    native_english: form.nativeEnglish === 'yes',
                    reading_proficiency: Number(form.readingLevel),
                    writing_proficiency: Number(form.writingLevel),
                    session_token: sessionToken,
                }),
            });

            if (!res.ok) {
                throw new Error('Failed to submit screening responses.');
            }

            // Mark screening as passed
            localStorage.setItem('screening_ok', '1');

            // // Go to consent page
            // const sp = new URLSearchParams();
            // sp.set('interface', interfaceParam);
            // navigate({
            //     pathname: '/consent',
            //     search: `?${sp.toString()}`,
            // });
            // Mark screening as passed
            localStorage.setItem('screening_ok', '1');

            // Next: registration (not consent)
            const nextPath = interfaceParam === 'annotate'
                ? '/register-evaluator'
                : '/register-creator';

            // Mark screening as passed
            localStorage.setItem('screening_ok', '1');
            const sp = new URLSearchParams(search || '');
            sp.set('interface', interfaceParam);

            navigate({
                pathname: interfaceParam === 'annotate' ? '/register-evaluator' : '/register-creator',
                search: `?${sp.toString()}`,
            });

        } catch (err) {
            console.error(err);
            setError(err.message || 'Server error while submitting screening.');
        } finally {
            setSubmitting(false);
        }
    };

    const readingOptions = [
        {value: '1', label: '1 - I am not at all proficient in English reading.'},
        {value: '2', label: '2 - I am somewhat proficient in English reading.'},
        {value: '3', label: '3 - I am proficient in English reading.'},
        {value: '4', label: '4 - I am very proficient in English reading.'},
        {value: '5', label: '5 - I am highly proficient in English reading.'},
    ];

    const writingOptions = [
        {value: '1', label: '1 - I am not at all proficient in English writing.'},
        {value: '2', label: '2 - I am somewhat proficient in English writing.'},
        {value: '3', label: '3 - I am proficient in English writing.'},
        {value: '4', label: '4 - I am very proficient in English writing.'},
        {value: '5', label: '5 - I am highly proficient in English writing.'},
    ];

    return (
        <div className="registration-page">
            <div className="registration-card">
                <button className="reg-back-link" onClick={handleBackHome}>
                    ← Back to Home
                </button>

                <div className="registration-header">
                    <div>
                        <h1 className="registration-title">Screening Questions</h1>
                        <p className="registration-subtitle">
                            Please answer these questions to confirm your eligibility for the study.
                        </p>
                    </div>
                </div>

                {blocked && (
                    <div className="reg-error">
                        Unfortunately, you are not eligible to participate in this study
                        because you are under 18 years of age.
                    </div>
                )}

                {error && !blocked && (
                    <div className="reg-error">
                        {error}
                    </div>
                )}

                <form className="registration-form" onSubmit={handleSubmit}>
                    {/* 1. Age 18+ */}
                    <div className="reg-field">
                        <div className="reg-label-row">
            <span className="reg-label">
              Are you 18 years of age or older?
            </span>
                            <span className="reg-required">*</span>
                        </div>
                        <div className="reg-radio-group">
                            <label className="reg-radio-option">
                                <input
                                    type="radio"
                                    name="age18"
                                    value="yes"
                                    checked={form.age18 === 'yes'}
                                    onChange={() => updateField('age18', 'yes')}
                                />
                                <span>Yes</span>
                            </label>
                            <label className="reg-radio-option">
                                <input
                                    type="radio"
                                    name="age18"
                                    value="no"
                                    checked={form.age18 === 'no'}
                                    onChange={() => updateField('age18', 'no')}
                                />
                                <span>No</span>
                            </label>
                        </div>
                    </div>

                    {/* 2. Native English speaker */}
                    <div className="reg-field">
                        <div className="reg-label-row">
            <span className="reg-label">
              Are you a native English speaker? (English as your first language)
            </span>
                            <span className="reg-required">*</span>
                        </div>
                        <div className="reg-radio-group">
                            <label className="reg-radio-option">
                                <input
                                    type="radio"
                                    name="nativeEnglish"
                                    value="yes"
                                    checked={form.nativeEnglish === 'yes'}
                                    onChange={() => updateField('nativeEnglish', 'yes')}
                                />
                                <span>Yes</span>
                            </label>
                            <label className="reg-radio-option">
                                <input
                                    type="radio"
                                    name="nativeEnglish"
                                    value="no"
                                    checked={form.nativeEnglish === 'no'}
                                    onChange={() => updateField('nativeEnglish', 'no')}
                                />
                                <span>No</span>
                            </label>
                        </div>
                    </div>

                    {/* 3. Reading proficiency */}
                    <div className="reg-field">
                        <div className="reg-label-row">
            <span className="reg-label">
              Please rate your proficiency in English reading.
            </span>
                            <span className="reg-required">*</span>
                        </div>
                        <div className="reg-radio-group">
                            {readingOptions.map(opt => (
                                <label key={opt.value} className="reg-radio-option">
                                    <input
                                        type="radio"
                                        name="readingLevel"
                                        value={opt.value}
                                        checked={form.readingLevel === opt.value}
                                        onChange={() => updateField('readingLevel', opt.value)}
                                    />
                                    <span>{opt.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* 4. Writing proficiency */}
                    <div className="reg-field">
                        <div className="reg-label-row">
            <span className="reg-label">
              Please rate your proficiency in English writing.
            </span>
                            <span className="reg-required">*</span>
                        </div>
                        <div className="reg-radio-group">
                            {writingOptions.map(opt => (
                                <label key={opt.value} className="reg-radio-option">
                                    <input
                                        type="radio"
                                        name="writingLevel"
                                        value={opt.value}
                                        checked={form.writingLevel === opt.value}
                                        onChange={() => updateField('writingLevel', opt.value)}
                                    />
                                    <span>{opt.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="reg-footer">
          <span className="reg-status">
            {submitting ? 'Submitting…' : ''}
          </span>
                        <button
                            type="submit"
                            className="reg-button-primary"
                            disabled={submitting || blocked}
                        >
                            Continue
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

    export default ScreeningPage;